<div class="page-content">
  <div class="head">Clients</div>
  <div class="subtext">
    Manage client onboarding, enabled agents, and archive actions from one workspace.
  </div>

  <div class="summary-with-spacing">
    <section class="summary-grid">
      <article class="summary-card">
        <span class="summary-card__label">Active</span>
        <strong class="summary-card__value">{{ activeClients }}</strong>
      </article>
      <article class="summary-card">
        <span class="summary-card__label">Archived</span>
        <strong class="summary-card__value">{{ archivedClients }}</strong>
      </article>
    </section>
  </div>

  <section class="clients-card">
    <div class="clients-card__top">
      <div class="clients-card__top-title">
        <h2>Client Directory</h2>
      </div>
    </div>

    <div class="clients-card__search">
      <label class="client-search">
        <span class="client-search__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Zm0-2a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Z"
              fill="#98A2B3" />
            <path
              d="m16.5 16.5 4.2 4.2"
              stroke="#98A2B3"
              stroke-width="2"
              stroke-linecap="round" />
          </svg>
        </span>
        <input
          type="search"
          class="client-search__input"
          [(ngModel)]="searchQuery"
          (ngModelChange)="onSearchQueryChange()"
          placeholder="Search clients by name, code, agents, or status…"
          autocomplete="off"
          aria-label="Search clients" />
      </label>
    </div>

    <div class="filter-tabs-wrap">
      <div class="filter-tabs-row">
        <div
          class="filter-tabs"
          [class.filter-tabs--archived]="selectedTab === 'archived'"
          role="tablist"
          aria-label="Client status">
          <span class="filter-tabs__slider" aria-hidden="true"></span>
          <button
            type="button"
            class="filter-tabs__btn"
            role="tab"
            [attr.aria-selected]="selectedTab === 'active'"
            (click)="setTab('active')">
            Active
          </button>
          <button
            type="button"
            class="filter-tabs__btn"
            role="tab"
            [attr.aria-selected]="selectedTab === 'archived'"
            (click)="setTab('archived')">
            Archived
          </button>
        </div>
        <button type="button" class="button filter-tabs-row__add" (click)="addClient()">
          Add Client
        </button>
      </div>
    </div>

    <div class="action-banner" *ngIf="actionMessage">
      {{ actionMessage }}
    </div>

    <div class="table-state" *ngIf="isLoading">Loading clients...</div>
    <div class="table-state table-state--error" *ngIf="!isLoading && loadError">
      {{ loadError }}
    </div>

    <div class="table-wrapper">
      <table class="clients-table" *ngIf="!isLoading && visibleClients.length">
        <thead>
          <tr>
            <th scope="col">Client</th>
            <th scope="col" class="clients-table__users-col">Users</th>
            <th scope="col">Client Code</th>
            <th scope="col">Enabled Agents</th>
            <th scope="col">Status</th>
            <th scope="col">Created On</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let client of pagedClients">
            <td>
              <div class="client-name">
                <ng-container *ngIf="showClientNameLink(client); else clientNamePlain">
                  <button
                    type="button"
                    class="client-link"
                    (click)="editClient(client)"
                    [attr.aria-label]="'Open ' + client.clientName + ' for editing'">
                    {{ client.clientName }}
                  </button>
                </ng-container>
                <ng-template #clientNamePlain>
                  <span class="client-name__text">{{ client.clientName }}</span>
                </ng-template>
              </div>
            </td>
            <td class="clients-table__users-col">
              <button
                type="button"
                class="users-count-btn"
                (click)="goToClientUsers(client)"
                [attr.aria-label]="userCountAriaLabelFor(client)">
                {{ userCountLabelFor(client) }}
              </button>
            </td>
            <td class="client-code">
              <div class="client-code__row">
                <span class="client-code__value">{{ client.clientCode }}</span>
                <button
                  type="button"
                  class="client-code__copy"
                  (click)="copyClientCode(client.clientCode)"
                  [attr.aria-label]="'Copy client code ' + client.clientCode"
                  title="Copy client code">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path
                      d="M8 4.5h11A1.5 1.5 0 0 1 20.5 6v11a1.5 1.5 0 0 1-1.5 1.5H8A1.5 1.5 0 0 1 6.5 17V6A1.5 1.5 0 0 1 8 4.5Z"
                      stroke="currentColor"
                      stroke-width="1.5" />
                    <path
                      d="M5 7.5h-1A1.5 1.5 0 0 0 2.5 9v10A1.5 1.5 0 0 0 4 20.5h10a1.5 1.5 0 0 0 1.5-1.5v-1"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round" />
                  </svg>
                </button>
              </div>
            </td>
            <td>
              <div class="agent-tags">
                <span class="agent-tag" *ngFor="let agent of client.enabledAgents">
                  {{ agent }}
                </span>
              </div>
            </td>
            <td>
              <span
                class="status-pill"
                [ngClass]="{
                  'status-pill--active': client.status === 'Active',
                  'status-pill--draft': client.status === 'Draft',
                  'status-pill--archived': client.status === 'Archived'
                }">
                {{ client.status }}
              </span>
            </td>
            <td>{{ client.createdOn }}</td>
            <td>
              <div class="row-actions" *ngIf="client.status === 'Archived'; else activeRowActions">
                <button type="button" class="inline-button" (click)="unarchiveClient(client)">
                  Unarchive
                </button>
              </div>
              <ng-template #activeRowActions>
                <div class="row-actions">
                  <button type="button" class="inline-button" (click)="editClient(client)">View</button>
                  <button type="button" class="inline-button inline-button--danger" (click)="archiveClient(client)">
                    Archive
                  </button>
                </div>
              </ng-template>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="table-pagination" *ngIf="!isLoading && !loadError && visibleClients.length">
        <span class="table-pagination__info">{{ pageRangeStart }}–{{ pageRangeEnd }} of {{ visibleClients.length }}</span>
        <div class="table-pagination__controls">
          <label class="table-pagination__size">
            Rows
            <select [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event)">
              <option *ngFor="let n of pageSizeOptions" [ngValue]="n">{{ n }}</option>
            </select>
          </label>
          <button type="button" class="inline-button" [disabled]="pageIndex === 0" (click)="prevPage()">Previous</button>
          <span class="table-pagination__page">{{ pageIndex + 1 }} / {{ totalPages }}</span>
          <button type="button" class="inline-button" [disabled]="pageIndex >= totalPages - 1" (click)="nextPage()">Next</button>
        </div>
      </div>
    </div>

    <div class="table-state" *ngIf="!isLoading && !loadError && !visibleClients.length">
      {{ emptyDirectoryHint }}
    </div>
  </section>

</div>
